import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from './supabaseClient'
import './AthleteProfile.css'

export default function AthleteProfile() {
  const { slug }          = useParams()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('slug', slug)
        .eq('is_public', true)
        .single()
      if (!data) { setNotFound(true) }
      else        { setProfile(data) }
      setLoading(false)
    }
    load()
  }, [slug])

  useEffect(() => {
    if (profile?.full_name) {
      document.title = `${profile.full_name} — CommitHQ`
    }
  }, [profile])

  if (loading) return <LoadingScreen />
  if (notFound) return <NotFound />
  return <ProfilePage profile={profile} />
}

// ── Loading ───────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className="ap-shell ap-center">
      <Logo />
      <p className="ap-loading-text">Loading profile…</p>
    </div>
  )
}

// ── Not found ─────────────────────────────────────────────────────

function NotFound() {
  return (
    <div className="ap-shell ap-center">
      <Logo />
      <div className="ap-notfound">
        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <h1 className="ap-notfound-title">Profile not found</h1>
        <p className="ap-notfound-sub">
          This profile doesn't exist or has been set to private.
        </p>
        <a className="ap-notfound-link" href="https://commithq.app">
          ← Back to CommitHQ
        </a>
      </div>
    </div>
  )
}

// ── Main profile page ─────────────────────────────────────────────

function ProfilePage({ profile }) {
  const hasPhysical  = profile.height || profile.weight || profile.bats || profile.throws
  const hasAcademics = true
  const hasSocial    = profile.instagram || profile.twitter
  const hasRecruiting = profile.perfect_game_url || profile.prep_baseball_url
  const hasCoaches   = profile.hs_coach_last_name || profile.travel_coach_last_name

  return (
    <div className="ap-page">
      <a href="/" className="ap-back">&#8592; Back to Home</a>
      <main className="ap-main">
        {/* ── Hero ── */}
        <section className="ap-hero">
          <div className="ap-avatar">
            {profile.avatar_url ? (
              <img src={profile.avatar_url} alt={profile.full_name || 'Profile'} className="ap-avatar-img" />
            ) : (
              avatarInitials(profile.full_name)
            )}
          </div>
          <div className="ap-hero-info">
            <h1 className="ap-name">{profile.full_name}</h1>
            <div className="ap-hero-badges">
              {profile.grad_year && (
                <span className="ap-badge ap-badge-year">
                  Class of {profile.grad_year}
                </span>
              )}
              {profile.position && (
                <span className="ap-badge ap-badge-pos">{profile.position}</span>
              )}
              {profile.contact_email && (
                <a href={`mailto:${profile.contact_email}`} className="ap-contact-btn">
                  📧 Contact Me
                </a>
              )}
            </div>
            {(profile.high_school || profile.hometown) && (
              <p className="ap-hero-location">
                {[profile.high_school, profile.hometown].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </section>

        {/* ── Physical stats ── */}
        {hasPhysical && (
          <section className="ap-stats-strip">
            {profile.height && <StatCell label="Height" value={profile.height} />}
            {profile.weight && <StatCell label="Weight" value={profile.weight} />}
            {profile.bats   && <StatCell label="Bats"   value={batLabel(profile.bats)} />}
            {profile.throws && <StatCell label="Throws" value={throwLabel(profile.throws)} />}
          </section>
        )}

        {/* ── Academic stats ── */}
        {hasAcademics && (
          <section className="ap-stats-strip ap-stats-academic">
            {profile.gpa       && <StatCell label="GPA" value={profile.gpa} />}
            <StatCell label="SAT" value={profile.sat_score || 'N/A'} />
            <StatCell label="ACT" value={profile.act_score || 'N/A'} />
          </section>
        )}

        {/* ── Bio ── */}
        {profile.bio && (
          <section className="ap-section">
            <h2 className="ap-section-label">About</h2>
            <p className="ap-bio">{profile.bio}</p>
          </section>
        )}

        {/* ── Video ── */}
        {profile.video_url && (
          <section className="ap-section ap-section-video">
            <h2 className="ap-section-label">Highlight Video</h2>
            <div className="ap-video-wrap">
              <video controls src={profile.video_url} preload="metadata" />
            </div>
          </section>
        )}

        {/* ── Coaches ── */}
        {hasCoaches && (
          <section className="ap-section">
            <h2 className="ap-section-label">Coaches</h2>
            <div className="ap-coaches-grid">
              {profile.hs_coach_last_name && (
                <div className="ap-coach-card">
                  <span className="ap-coach-type">High School Coach</span>
                  <span className="ap-coach-name">
                    {[profile.hs_coach_first_name, profile.hs_coach_last_name].filter(Boolean).join(' ')}
                  </span>
                  {profile.hs_coach_email && (
                    <a href={`mailto:${profile.hs_coach_email}`} className="ap-coach-contact">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      {profile.hs_coach_email}
                    </a>
                  )}
                  {profile.hs_coach_phone && (
                    <a href={`tel:${profile.hs_coach_phone}`} className="ap-coach-contact">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.36 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      {profile.hs_coach_phone}
                    </a>
                  )}
                </div>
              )}
              {profile.travel_coach_last_name && (
                <div className="ap-coach-card">
                  <span className="ap-coach-type">Travel Coach</span>
                  <span className="ap-coach-name">
                    {[profile.travel_coach_first_name, profile.travel_coach_last_name].filter(Boolean).join(' ')}
                  </span>
                  {profile.travel_coach_email && (
                    <a href={`mailto:${profile.travel_coach_email}`} className="ap-coach-contact">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      {profile.travel_coach_email}
                    </a>
                  )}
                  {profile.travel_coach_phone && (
                    <a href={`tel:${profile.travel_coach_phone}`} className="ap-coach-contact">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.36 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      {profile.travel_coach_phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── Recruiting profiles ── */}
        {hasRecruiting && (
          <section className="ap-section">
            <h2 className="ap-section-label">Recruiting Profiles</h2>
            <div className="ap-recruiting-badges">
              {profile.perfect_game_url && (
                <a
                  href={profile.perfect_game_url}
                  className="ap-rec-badge ap-rec-badge-pg"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Perfect Game Profile
                  <ExternalIcon />
                </a>
              )}
              {profile.prep_baseball_url && (
                <a
                  href={profile.prep_baseball_url}
                  className="ap-rec-badge ap-rec-badge-pbr"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4l3 3"/>
                  </svg>
                  Prep Baseball Report
                  <ExternalIcon />
                </a>
              )}
            </div>
          </section>
        )}

        {/* ── Social ── */}
        {hasSocial && (
          <section className="ap-section">
            <h2 className="ap-section-label">Social</h2>
            <div className="ap-social">
              {profile.instagram && (
                <a
                  href={`https://instagram.com/${profile.instagram.replace(/^@/, '')}`}
                  className="ap-social-link ap-social-ig"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5"/>
                    <circle cx="12" cy="12" r="4"/>
                    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                  </svg>
                  @{profile.instagram.replace(/^@/, '')}
                </a>
              )}
              {profile.twitter && (
                <a
                  href={`https://x.com/${profile.twitter.replace(/^@/, '')}`}
                  className="ap-social-link ap-social-x"
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  @{profile.twitter.replace(/^@/, '')}
                </a>
              )}
            </div>
          </section>
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="ap-footer">
        <span>Built with</span>
        <a href="https://commithq.app" className="ap-footer-link">CommitHQ</a>
      </footer>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────

function StatCell({ label, value }) {
  return (
    <div className="ap-stat">
      <span className="ap-stat-label">{label}</span>
      <span className="ap-stat-value">{value}</span>
    </div>
  )
}

function ExternalIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  )
}

function Logo() {
  return (
    <svg width="32" height="36" viewBox="0 0 64 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="64" height="72" rx="10" fill="#0D2340"/>
      <rect width="64" height="10" fill="#1D9E75"/>
      <rect width="64" height="5" y="5" fill="#1D9E75"/>
      <text x="32" y="50" textAnchor="middle" fontFamily="Georgia, serif" fontSize="34" fontWeight="bold" fill="white">C</text>
      <text x="32" y="64" textAnchor="middle" fontFamily="Georgia, serif" fontSize="11" fontWeight="bold" fill="#1D9E75">HQ</text>
    </svg>
  )
}

function avatarInitials(name) {
  return (name || '').trim().split(/\s+/).map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2) || '?'
}

function batLabel(b)   { return b === 'S' ? 'Switch' : b === 'L' ? 'Left' : 'Right' }
function throwLabel(t) { return t === 'L' ? 'Left' : 'Right' }
